import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import IconPicker from "./IconPicker";
import RoutePicker from "./RoutePicker";
import { normaliseUrl } from "../utilities/routeOptions";

/** A single available placeholder item: click to add, ⋮ menu to edit/delete. */
function AvailableChip({ item, onAdd, onEdit, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  const isPlaceholder = !!item?.FlagPlaceholder;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", m: 0.5 }}>
      <Chip
        icon={item?.Icon ? <i className={`${item.Icon}`} style={{ fontSize: 12, marginLeft: 8 }} /> : undefined}
        label={item?.Name}
        onClick={() => onAdd(item)}
        color={isPlaceholder ? "warning" : "primary"}
        variant="outlined"
        clickable
        sx={{ borderTopRightRadius: isPlaceholder ? 0 : undefined, borderBottomRightRadius: isPlaceholder ? 0 : undefined }}
      />
      {isPlaceholder && (
        <>
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ border: "1px solid", borderColor: "warning.light", borderLeft: 0, borderRadius: 0, borderTopRightRadius: 16, borderBottomRightRadius: 16, p: 0.25 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onEdit(item);
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onDelete(item?.Id);
              }}
              sx={{ color: "error.main" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
}

export default function AddItemPanel({ items, onAdd, onCreate, onEdit, onDelete }) {
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ Icon: "", Name: "", Url: "" });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((i) => i?.Name?.toLowerCase().includes(q));
  }, [items, search]);

  const submitCreate = () => {
    if (!draft.Name.trim()) {
      toast.error("Please enter a menu item name");
      return;
    }
    if (!draft.Icon) {
      toast.error("Please choose an icon");
      return;
    }
    onCreate({
      Icon: draft.Icon,
      Name: draft.Name.trim(),
      Url: normaliseUrl(draft.Url) || "#",
    });
    setDraft({ Icon: "", Name: "", Url: "" });
    setShowCreate(false);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Available items
        </Typography>
        {/* Task 24886 - the Global Menus builder draws its destinations from
            the master catalogue, which is maintained on Super Menu, not here.
            No onCreate handler means no button, rather than a button that
            throws when you submit it. */}
        {onCreate && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant={showCreate ? "contained" : "outlined"}
            onClick={() => setShowCreate((s) => !s)}
          >
            New item
          </Button>
        )}
      </Stack>

      <Collapse in={showCreate}>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.default",
          }}
        >
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label="Name"
              value={draft.Name}
              onChange={(e) => setDraft((d) => ({ ...d, Name: e.target.value }))}
              autoFocus
              fullWidth
            />
            <IconPicker
              value={draft.Icon}
              onChange={(icon) => setDraft((d) => ({ ...d, Icon: icon }))}
            />
            <RoutePicker
              value={draft.Url}
              onChange={(url) => setDraft((d) => ({ ...d, Url: url }))}
            />
            <Button variant="contained" color="success" onClick={submitCreate}>
              Add item
            </Button>
          </Stack>
        </Box>
      </Collapse>

      <TextField
        size="small"
        fullWidth
        placeholder="Search items"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mt: 2 }}
        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.disabled" }} /> }}
      />

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        Click an item to add it to the menu, then drag to position it.
      </Typography>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          flexWrap: "wrap",
          alignContent: "flex-start",
          minHeight: 60,
          maxHeight: 360,
          overflowY: "auto",
          p: 0.5,
          borderRadius: 2,
          bgcolor: "action.hover",
        }}
      >
        {filtered?.length ? (
          filtered.map((item) => (
            <AvailableChip
              key={item?.Id}
              item={item}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))
        ) : (
          <Typography variant="body2" sx={{ p: 1.5, color: "text.disabled" }}>
            {items?.length ? "No items match your search." : "All items are in the menu."}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
